import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { StaffApiService } from 'v2/modules/shared';
import { OptionInterface } from 'v2/modules/shared/interfaces/common.interface';
import { EUserRoles } from 'v2/modules/shared/interfaces/user-role.interface';
import { ClientTypeApiService } from 'v2/modules/shared/services/api/client/client-type-api.service';

@Component({
  selector: 'lum-client-profile-info-form',
  templateUrl: './client-profile-info-form.component.html',
  styleUrls: ['./client-profile-info-form.component.scss'],
})
export class ClientProfileInfoFormComponent implements OnInit {
  @Input()
  form :UntypedFormGroup;

  @Output()
  tabChange = new EventEmitter<{ name :string; index :number }>();

  @Output()
  tabValidation = new EventEmitter<{ invalid :boolean; nextTab :string }>();

  clientTypes$ :Observable<OptionInterface[]>;
  salesRepAssignees$ :Observable<OptionInterface[]>;
  clientTypeList :OptionInterface[] = [];
  salesRepAssigneesList :OptionInterface[] = [];
  allowedRoles :string = [EUserRoles.SALES_MEMBER, EUserRoles.ADMIN].join(',');

  private onDestroy$ = new Subject();

  constructor(
    private clientTypeApiService :ClientTypeApiService,
    private staffApiService :StaffApiService,
  ) {}

  ngOnInit() :void {
    this.getDataFromService();
    this.clientTypeApiService.get({});
    this.getTypesFromObservable().subscribe();
    this.staffApiService.getStaffs({ roles: this.allowedRoles });
    this.getSalesRepAssigneesFromObservable().subscribe();
  }
  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onChangeTab(direction :string) :void {
    if (direction == 'previous') {
      this.onSetTab('client-info', 0);
    } else {
      this.onSetTab('business-address', 1);
    }
  }
  onSetTab(name :string, index :number) :void {
    this.tabChange.emit({
      name,
      index,
    });
  }
  onSetTags(tags :any) :void {
    this.form?.get('tags')?.patchValue(tags?.tag);
  }
  onSelectClientType(event :MatSelectChange) :void {
    const index = this.clientTypeList.findIndex(
      (type) => type.value === event.value,
    );
    this.form?.get('business_type')?.patchValue(this.clientTypeList[index]?.label);
  }
  onSelectRepAssignees(staffs :OptionInterface[]) :void {
    this.form?.get('salesRepAssignee')?.patchValue(staffs);
  }
  onSubmit() :void {
    if (!this.hasInvalidInput) {
      this.onChangeTab('next');
    }
  }

  private getDataFromService() :void {
    this.clientTypes$ = this.clientTypeApiService.types$;
    this.salesRepAssignees$ = this.staffApiService.staffs$;
  }
  private getTypesFromObservable() :Observable<OptionInterface[]> {
    return this.clientTypes$.pipe(
      tap((types :any) => {
        if (types.length > 0) {
          this.clientTypeList = types.map((list :any) => ({
            value: list.id,
            label: list.type,
          }));
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }
  private getSalesRepAssigneesFromObservable() :Observable<OptionInterface[]> {
    return this.salesRepAssignees$.pipe(
      tap((staffs :any) => {
        if (staffs.length > 0) {
          this.salesRepAssigneesList = staffs.map((staff :any) => ({
            value: {
              id: staff.id,
              firstname: staff.firstname,
              lastname: staff.lastname,
              email: staff.email,
            },
            label: `${staff.firstname} ${staff.lastname} (${staff.email})`,
          }));
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get hasInvalidInput() :boolean {
    const invalid =
      this.form?.controls['business_name']?.invalid ||
      this.form?.controls['fein']?.invalid;
    this.tabValidation.emit({
      invalid,
      nextTab: 'business-address',
    });

    return invalid;
  }
  get clientType() :OptionInterface | null {
    if (this.clientTypeList) {
      const clientTypeValue = this.form?.get('business_type_id')?.value;
      const index = this.clientTypeList.findIndex(
        (t) => t.value === clientTypeValue,
      );

      return this.clientTypeList[index];
    }

    return null;
  }
  get salesRepAssigneeIds() :number[] {
    return this.form?.get('salesRepAssignee')?.value ?? [];
  }
  get notes() :string {
    return this.form?.get('notes')?.value ?? '';
  }
  get tags() :number[] {
    return this.form?.get('tags')?.value ?? [];
  }
  get businessNameControl() :UntypedFormControl {
    return this.form.get('business_name') as UntypedFormControl;
  }
  get feinControl() :UntypedFormControl {
    return this.form.get('fein') as UntypedFormControl;
  }
}
